import React, { createRef, useEffect, useRef, useState } from "react";
import { Layer, Map, MapRef, Source } from "react-map-gl";
import maplibregl from "maplibre-gl";
import {
  isMapboxURL,
  transformMapboxUrl,
} from "maplibregl-mapbox-request-transformer";
import "intersection-observer";
import "maplibre-gl/dist/maplibre-gl.css";
import "../styles/app.scss";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import classnames from "classnames";
import { Paragraph, splitParagraphs } from "src/pages/Homepage";

/**
 * This is a public access token connected to THE CITY's MapBox account:
 */
export const MAPBOX_TOKEN =
  "pk.eyJ1IjoidGhlLWNpdHkiLCJhIjoiY2xhMWVsNDY3MDJoYTNya2ptYWZpZW15dyJ9.iv4eTGq5GylMTUcYH16Big";

/**
 * This is a link to our custom MapBox Studio basemap style:
 */
export const MAPBOX_STYLE_URL =
  "mapbox://styles/the-city/cloq9dvvp00ey01qn5jkgcqzb";

/**
 * Since our custom style does not include links for map sprites, let's specify a fallback map style to load in:
 */
const FALLBACK_STYLE_URL_ROOT =
  "https://tiles.basemaps.cartocdn.com/gl/positron-gl-style/sprite@2x";

export const transformStyleRequest = (url: string, resourceType: string) => {
  if (resourceType === "SpriteImage") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.png`,
    };
  }
  if (resourceType === "SpriteJSON") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.json`,
    };
  }
  if (isMapboxURL(url)) {
    return transformMapboxUrl(url, resourceType, MAPBOX_TOKEN);
  }
  return { url };
};

export const MapLoadingScreen = () => (
  <div className="loading-screen map">
    <div>
      <p className="mb-2">Loading...</p>
      <progress className="progress is-medium" max="100" />
    </div>
  </div>
);

const NUMBER_OF_MAP_SLIDES = 3;
/**
 * When each paragraph gets within this distance (in px) of the top of the viewport, animations are triggered.
 */
const ANIMATION_TRIGGER_BUFFER = 500;

/**
 * Total length of time map animations take, in milliseconds.
 */
const ANIMATION_DURATION = 1000;

/**
 * A "blank" set of x and y scroll coordinates to initialize our react states with.
 * The values here are meaningless, just making sure the y coordinate is larger than our trigger buffer.
 */
const BLANK_SCROLL_POSITION = {
  x: 0,
  y: ANIMATION_TRIGGER_BUFFER + 100,
};

export const FirstMapSequence: React.FC<{
  slideContent: string;
}> = ({ slideContent }) => {
  const slideElements = splitParagraphs(slideContent);

  // Map data that goes on the map:

  const [boundaryData, setBoundaryData] = useState(null);
  /**
   * PRP stands for "Potentially Responsible Parties"
   */
  const [prpData, setPrpData] = useState(null);
  const [sewerData, setSewerData] = useState(null);

  /**
   * The number of the current slide the user is looking at, starting with 0:
   */
  const [currentSlide, setCurrentSlide] = useState(0);

  /**
   * The scroll positions of each of the paragraphs overlayed on the map:
   */
  const [paragraphPosition, setParagraphPosition] = useState({
    0: BLANK_SCROLL_POSITION,
    1: BLANK_SCROLL_POSITION,
    2: BLANK_SCROLL_POSITION,
  });

  /**
   * Add data to map on initial mount of the react component:
   */
  useEffect(() => {
    fetch("./data/newtown_creek_superfund.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setBoundaryData(json);
        console.log("Loaded boundary data");
      })
      .catch((err) => console.error("Could not load boundary data", err));
    fetch("./data/newtown_prps.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setPrpData(json);
        console.log("Loaded PRP data");
      })
      .catch((err) => console.error("Could not load PRP data", err));
    fetch("./data/newtown_sewer_overflows.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setSewerData(json);
        console.log("Loaded sewer overflow data");
      })
      .catch((err) => console.error("Could not load sewer overflow data", err));
  }, []);

  const mapRef = useRef<MapRef>();
  const paragraphRefs = useRef([createRef(), createRef(), createRef()]);

  /**
   * This custom React hook keeps track of a specific paragraph in our scrolly
   * and continuously updates its scroll position saved in state.
   */
  const useParagraphTracker = (paragraphNumber: number) =>
    useScrollPosition(
      ({ currPos }) => {
        setParagraphPosition((prevValues) => ({
          ...prevValues,
          [paragraphNumber]: currPos,
        }));
      },
      [],
      //@ts-ignore
      paragraphRefs.current[paragraphNumber]
    );

  // Sadly, the `useScrollPosition` hook can only be used one at a time to update
  // states based on the position of an HTML element. Therefore, we need to implement
  // this hook once for each paragraph we are tracking the location of:
  useParagraphTracker(0);
  useParagraphTracker(1);
  useParagraphTracker(2);

  /**
   * This hook determines which "current slide" the user is viewing by
   * looking at the scroll positions of each paragraph:
   */
  useEffect(() => {
    const currentSlide =
      (paragraphPosition[0].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0) +
      (paragraphPosition[1].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0) +
      (paragraphPosition[2].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0);

    setCurrentSlide(currentSlide);
  }, [paragraphPosition]);

  /**
   * This hook triggers custom animations based on when the current slide changes:
   */
  useEffect(() => {
    // Animate zoom level change when the first paragraph scrolls by:
    if (currentSlide === 1) {
      mapRef.current?.fitBounds(
        // Newtown Creek Bounds:
        [-73.972051, 40.706072, -73.909472, 40.747882],
        {
          duration: ANIMATION_DURATION,
        }
      );
    }
    if (currentSlide === 0) {
      mapRef.current?.zoomTo(10, { duration: ANIMATION_DURATION });
    }
  }, [currentSlide]);

  return !!boundaryData && !!prpData && !!sewerData ? (
    <div
      className="scrolly-container is-flex is-flex-direction-column is-justify-content-space-between is-align-items-center is-doubled"
      style={{
        height: `${200 + NUMBER_OF_MAP_SLIDES * 100}vh`,
      }}
    >
      <div className="scrolly-image">
        <div className="map-container is-flex is-flex-direction-column is-align-items-center is-justify-content-center">
          <Map
            //@ts-ignore
            ref={mapRef}
            mapLib={maplibregl}
            initialViewState={{
              longitude: -73.977708344928,
              latitude: 40.713323256573386,
              zoom: 10,
            }}
            mapStyle={MAPBOX_STYLE_URL}
            transformRequest={transformStyleRequest}
            mapboxAccessToken={MAPBOX_TOKEN}
            interactive={false}
            cursor="auto"
          >
            <Source id="boundary-data" type="geojson" data={boundaryData}>
              <Layer
                id="superfund-boundary"
                type="fill"
                source="boundary-data"
                paint={{
                  "fill-color": "#0086be",
                  "fill-opacity": currentSlide < 2 ? 1 : 0.3,
                }}
              />
            </Source>
            <Source id="prp-data" type="geojson" data={prpData}>
              <Layer
                id="prps"
                type="fill"
                source="prp-data"
                paint={{
                  "fill-color": "#fdbe2b",
                  "fill-opacity":
                    currentSlide < 2 ? 0 : currentSlide === 2 ? 1 : 0.3,
                }}
              />
            </Source>
            <Source id="sewer-data" type="geojson" data={sewerData}>
              <Layer
                id="sewer"
                type="circle"
                source="sewer-data"
                paint={{
                  "circle-color": "#8AD306",
                  "circle-radius": (currentSlide === 3 ? 1 : 0) * 15,
                  "circle-opacity": (currentSlide === 3 ? 1 : 0) * 0.8,
                  "circle-radius-transition": {
                    duration: 1500,
                  },
                }}
              />
            </Source>
          </Map>
        </div>
      </div>
      {slideElements.map((slide, i) => (
        <div
          key={i}
          className="scrolly-caption is-flex-direction-column is-justify-content-center p-3 mx-2"
          style={{
            marginTop: `${i + 1}00vh`,
          }}
          //@ts-ignore
          ref={paragraphRefs.current[i]}
        >
          <Paragraph text={slide} />
        </div>
      ))}
    </div>
  ) : (
    <MapLoadingScreen />
  );
};

export const SecondMapSequence: React.FC<{
  slideContent: string;
}> = ({ slideContent }) => {
  const slideElements = splitParagraphs(slideContent);
  // Map data that goes on the map:

  const [boundaryData, setBoundaryData] = useState(null);
  const [floodplainData, setFloodplainData] = useState(null);
  const [brownfieldData, setBrownfieldData] = useState(null);

  /**
   * The number of the current slide the user is looking at, starting with 0:
   */
  const [currentSlide, setCurrentSlide] = useState(0);

  /**
   * The scroll positions of each of the paragraphs overlayed on the map:
   */
  const [paragraphPosition, setParagraphPosition] = useState({
    0: BLANK_SCROLL_POSITION,
    1: BLANK_SCROLL_POSITION,
    2: BLANK_SCROLL_POSITION,
  });

  /**
   * Add data to map on initial mount of the react component:
   */
  useEffect(() => {
    fetch("./data/newtown_creek_superfund.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setBoundaryData(json);
        console.log("Loaded boundary data");
      })
      .catch((err) => console.error("Could not load boundary data", err));
    fetch("./data/floodplain_bk_qns.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setFloodplainData(json);
        console.log("Loaded floodplain data");
      })
      .catch((err) => console.error("Could not load floodplain data", err));
    fetch("./data/newtown_brownfield_sites.geojson", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setBrownfieldData(json);
        console.log("Loaded brownfield data");
      })
      .catch((err) => console.error("Could not load brownfield data", err));
  }, []);

  const mapRef = useRef<MapRef>();
  const paragraphRefs = useRef([createRef(), createRef(), createRef()]);

  // Sadly, the `useScrollPosition` hook can only be used one at a time to update
  // states based on the position of an HTML element. Therefore, we need to implement
  // this hook once for each paragraph we are tracking the location of:

  useScrollPosition(
    ({ currPos }) => {
      setParagraphPosition((prevValues) => ({
        ...prevValues,
        0: currPos,
      }));
    },
    [],
    //@ts-ignore
    paragraphRefs.current[0]
  );

  useScrollPosition(
    ({ currPos }) => {
      setParagraphPosition((prevValues) => ({
        ...prevValues,
        1: currPos,
      }));
    },
    [],
    //@ts-ignore
    paragraphRefs.current[1]
  );

  useScrollPosition(
    ({ currPos }) => {
      setParagraphPosition((prevValues) => ({
        ...prevValues,
        2: currPos,
      }));
    },
    [],
    //@ts-ignore
    paragraphRefs.current[2]
  );

  /**
   * This hook determines which "current slide" the user is viewing by
   * looking at the scroll positions of each paragraph:
   */
  useEffect(() => {
    const currentSlide =
      (paragraphPosition[0].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0) +
      (paragraphPosition[1].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0) +
      (paragraphPosition[2].y < ANIMATION_TRIGGER_BUFFER ? 1 : 0);

    setCurrentSlide(currentSlide);
  }, [paragraphPosition]);

  /**
   * This hook triggers custom animations based on when the current slide changes:
   */
  useEffect(() => {
    // Animate zoom level change when the first paragraph scrolls by:
    if (currentSlide === 1) {
      mapRef.current?.fitBounds(
        // Newtown Creek Bounds:
        [-73.972051, 40.706072, -73.909472, 40.747882],
        {
          duration: ANIMATION_DURATION,
        }
      );
    }
    if (currentSlide === 0) {
      mapRef.current?.zoomTo(12, { duration: ANIMATION_DURATION });
    }
  }, [currentSlide]);

  return !!boundaryData && !!floodplainData && !!brownfieldData ? (
    <div
      className={classnames(
        "scrolly-container",
        "is-flex",
        "is-flex-direction-column",
        "is-justify-content-space-between",
        "is-align-items-center",
        "is-doubled"
      )}
      style={{
        height: `${200 + NUMBER_OF_MAP_SLIDES * 100}vh`,
      }}
    >
      <div className="scrolly-image">
        <div className="map-container is-flex is-flex-direction-column is-align-items-center is-justify-content-center">
          <Map
            //@ts-ignore
            ref={mapRef}
            mapLib={maplibregl}
            initialViewState={{
              longitude: -73.93718452070624,
              latitude: 40.72729158407977,
              zoom: 12,
            }}
            mapStyle={MAPBOX_STYLE_URL}
            transformRequest={transformStyleRequest}
            mapboxAccessToken={MAPBOX_TOKEN}
            interactive={false}
            cursor="auto"
          >
            <Source id="boundary-data" type="geojson" data={boundaryData}>
              <Layer
                id="superfund-boundary"
                type="fill"
                source="boundary-data"
                paint={{
                  "fill-color": "#0086be",
                  "fill-opacity": currentSlide < 2 ? 1 : 0.3,
                }}
              />
            </Source>
            <Source id="flood-plain-data" type="geojson" data={floodplainData}>
              <Layer
                id="floodplain"
                type="fill"
                source="flood-plain-data"
                paint={{
                  "fill-color": "#214da5",
                  "fill-opacity":
                    currentSlide < 2 ? 0 : currentSlide === 2 ? 1 : 0.3,
                }}
              />
            </Source>
            <Source id="brownfield-data" type="geojson" data={brownfieldData}>
              <Layer
                id="brownfields"
                type="fill"
                source="brownfield-data"
                paint={{
                  "fill-color": "#fa7416",
                  "fill-opacity": currentSlide === 3 ? 1 : 0,
                }}
              />
            </Source>
          </Map>
        </div>
      </div>
      {slideElements.map((slide, i) => (
        <div
          key={i}
          className="scrolly-caption is-flex-direction-column is-justify-content-center p-3 mx-2"
          style={{
            marginTop: `${i + 1}00vh`,
          }}
          //@ts-ignore
          ref={paragraphRefs.current[i]}
        >
          <Paragraph text={slide} />
        </div>
      ))}
    </div>
  ) : (
    <MapLoadingScreen />
  );
};
